'use strict';

/* globals angular */
const _ = require('lodash');
/*
 * This module will determine the actions to take based on the url of the page,
 * and similarly will reflect back the actions when some of the components
 * require an action (like snaps and @lat-lng)
 * This can probably be moved to Map (depending on the interaction with
 * other components other than map that require routing), but will handle all
 * site, dcs, overlays, annotations and snaps
 * So state will be dash.map
 */
function DashRoutes($urlRouterProvider, $stateProvider, $urlMatcherFactoryProvider, $locationProvider) {
  /** Shared functions **/
  const handleSiteNameRoute = ($state, $match, Flux) => {
    const SiteStore = Flux.Store.get('SiteStore');
    const current = SiteStore.get('current');
    let notify = true;

    if (current && current.slug === $match.siteName) {
      notify = false;
    }

    $state.go('dash.map', {
      siteName: $match.siteName,
      layer: $match.layerKey
    }, {
      location: 'replace',
      notify: notify
    });
  };

  const kFullUserRequest = 'userSites,organizations.users.organizations.roles,organizations.projects.sites,organizations.plans.features';
  const kFastUserRequest = 'getFirstSite,organizations.plans.features';

  const handleDataCaptureRoute = ($state, $match, Flux) => {
    const SiteStore = Flux.Store.get('SiteStore');
    const current = SiteStore.get('current');
    let notify = true;
    if (current && current.slug === $match.siteName) {
      notify = false;
    }

    if ($state.includes('manager') || $state.includes('settings')) {
      // Reload state when coming back from manager or settings via the back button
      notify = true;
    };

    $state.go('dash.map', {
      siteName: $match.siteName,
      dataCaptureId: $match.dataCaptureId,
      layer: $match.layer
    }, {
      location: 'replace',
      notify: notify
    });
  };

  const valToString = (val) => {
    if (typeof val !== 'undefined' && val !== null) {
      return val.toString();
    }
    else {
      return val;
    }
  };

  const checkAuthentication = ($state, $q, $timeout, AuthService) => {
    const deferred = $q.defer();
    const authenticated = AuthService.isAuthenticated();
    const params = {
      location: ''
    };

    $timeout(() => {
      if (authenticated) {
        deferred.resolve();
      }
      else {
        $state.go('login', params);
        deferred.reject();
      }
    });

    return deferred.promise;
  };

  const loadOrganization = (Flux) => {
    const OrganizationStore = Flux.Store.get('OrganizationStore');
    const PlanStore = Flux.Store.get('PlanStore');

    return OrganizationStore.loadOrganization()
    .then(() => {
      return PlanStore.preloadOrgPlanData();
    });
  };

  /** Routing configurations **/
  $urlMatcherFactoryProvider.type('nonURIEncoded', {
    encode: valToString,
    decode: valToString,
    is: () => {
      return true;
    },
    pattern: /site\/\w+(?:\/dc\/\d+)?(\?([\w-]+(=[\w-]*)?(&[\w-]+(=[\w-]*)?)*)?$)?/
  });

  $locationProvider.html5Mode(true);
  $urlMatcherFactoryProvider.strictMode(false);

  $urlRouterProvider
  .when('/', [
    '$state',
    ($state) => {
      $state.go('dash.map', {}, {
        location: 'replace'
      });
    }
  ])
  .when('/site', [
    '$state', '$match', 'sbFlux',
    handleSiteNameRoute
  ])
  .when('/site/:siteName', [
    '$state', '$match', 'sbFlux',
    handleSiteNameRoute
  ])
  .when('/site/:siteName/dc', [
    '$state', '$match', 'sbFlux',
    handleDataCaptureRoute
  ])
  .when('/site/:siteName/dc/:dataCaptureId', [
    '$state', '$match', 'sbFlux',
    handleDataCaptureRoute
  ])
  .when('/site/:siteName/dc/:dataCaptureId/layer/:layer', [
    '$state', '$match', 'sbFlux',
    handleDataCaptureRoute
  ])
  .otherwise(($injector) => {
    console.warn('Otherwise routing rule');
    const $state = $injector.get('$state');
    $state.go('404');
  });

  $stateProvider
  .state('dash.map', {
    // https://github.com/angular-ui/ui-router/issues/1119#issuecomment-64696060
    url: '{changeToLocation:nonURIEncoded}',

    // UI-router documentation is a really obscure place...
    // ALL parameters that want to be passed in `$state.go` must be explicitely
    // defined here, otherwise they won't be available in `$stateParams`
    // http://stackoverflow.com/a/26204095/204142
    params: {
      siteName: null,
      dataCaptureId: null,
      changeToLocation: null,
      urlUpdate: null,
      layer: null
    },
    data: {
      customData: null
    },
    resolve: {
      authenticate: [
        '$location', '$q', '$state', '$stateParams', '$timeout', 'AuthService', 'IdentityService', 'ManagerStore', 'PlanStore', 'ResourceHelper', 'UserStore', 'MetricsService', '$rootScope',
        ($location, $q, $state, $stateParams, $timeout, AuthService, IdentityService, ManagerStore, PlanStore, ResourceHelper, UserStore, MetricsService, $rootScope) => {

          if ($stateParams.urlUpdate) {
            return;
          }

          const deferred = $q.defer();
          const authenticated = AuthService.isAuthenticated();
          const params = {
            location: $location.path() === '/' ? '': $location.absUrl()
          };

          // $timeout is another of those ng 'hacks'...
          // http://stackoverflow.com/questions/29811045/how-to-redirect-in-a-ui-router-resolve
          $timeout(() => {
            if (authenticated) {
              const user = IdentityService.getUser();
              // a user with multiple orgs will have selected their org already. for users with one org, select that org.
              const org = IdentityService.getCurrentOrganization() || user.organizations[0];
              // FIXME: Define the properties that an 'anonymous' user will have and
              // document them so it is standard and congruent with normal Users
              if (user && (!user.group || user.group !== 'anon')) {
                // TODO: it would be nice to check to see if the user actually requested a change in organization and only do this api call if true
                const User = ResourceHelper.getResource('User');

                let splitPath = $location.path().split('/');

                if ($stateParams.changeToLocation) {
                  splitPath = $stateParams.changeToLocation.split('/');
                }

                const currentPath = splitPath[1];
                const currentSlug = splitPath[2];

                const lastVisitedSiteSlug = IdentityService.get('lastVisitedSiteSlug');
                const defaultSiteSlug = currentSlug || lastVisitedSiteSlug;

                // we will use `firstSiteRequest` to determine how are we going to request the initial set of sites

                User.find(user.id, {
                  params: {
                    include: kFastUserRequest,
                    orgId: org.id,
                    admin: user.isAdmin, // Hint for asking for full sites. Validation is done in backend.
                    deleted: true, // Workaround for the 'ambiguous' column on userSites
                                   // See: sky-lib@User#userSites
                    includeDisabled: true // (Plan isEnabled) We want to show dunning / exipration and maybe allow ReadOnly access

                  },
                  bypassCache: false,
                  cacheResponse: true
                })
                .then(userRecord => {
                  // we don't need to load any sites right now


                  const SiteResource = ResourceHelper.getResource('Site');

                  // nothing to ask for
                  if (!defaultSiteSlug) {
                    return userRecord;
                  }

                  return new Promise(resolve => {
                    SiteResource.findBySlug(defaultSiteSlug)
                    .then(site => {

                      userRecord.userSites = [site];
                      resolve(userRecord);
                    })
                    .catch(err => {

                      // there was an error fetching the site, but lets return the userRecord and use the default site
                      console.warn(err);
                      resolve(userRecord);
                    });
                  });
                })
                .then((userRecord) => {

                  const currentOrganization = IdentityService.get('currentOrganization');
                  const isCurrentSiteInOrg = _.find(userRecord.userSites, {
                    slug: defaultSiteSlug
                  });

                  let newPath;
                  let orgWithRelated;

                  if (!userRecord.userSites || !userRecord.userSites.length) {
                    $state.go('no-projects');
                    deferred.reject();
                  }

                  // if the user is on a site and somehow loses their currentOrganization token then refreshes the page,
                  // this makes sure that the page they're reloading is still in their default organization
                  // if not, we need to redirect the user to a site in their default organization, so we choose a default site
                  // we also check that it's a site path being requested to allow settings and login pages to reach dashboard.
                  if (currentPath === 'site' && !isCurrentSiteInOrg) {
                    newPath = `/site/${defaultSiteSlug || userRecord.userSites[0].slug}`;
                    $location.path(newPath);
                  }

                  // here we are going to download the entire site list
                  let alreadyLoadedRemainingData = false;

                  let loadRemainingData = function() {

                    if (alreadyLoadedRemainingData) {
                      return;
                    }

                    alreadyLoadedRemainingData = true;

                    User.find(user.id, {
                      params: {
                        include: kFullUserRequest,
                        orgId: org.id,
                        admin: user.isAdmin, // Hint for asking for full sites. Validation is done in backend.
                        deleted: true, // Workaround for the 'ambiguous' column on userSites
                                       // See: sky-lib@User#userSites
                        includeDisabled: true // (Plan isEnabled) We want to show dunning / exipration and maybe allow ReadOnly access
                      },
                      bypassCache: true,
                      cacheResponse: !IdentityService.isAdminUser()
                    })
                    .then((userRecordWithSites) => {

                      $rootScope.allUserDataReady = true;

                      let orgCopy;
                      orgWithRelated = currentOrganization ? _.find(userRecord.organizations, {id: currentOrganization.id}): userRecord.organizations[0];
                      orgCopy = _.extend({}, orgWithRelated);
                      delete orgCopy.projects;
                      delete orgCopy.users;
                      PlanStore.setOrgPlanFeatures(_.find(orgCopy.plans, (plan) => { // this case should not exist [Bad Data]
                        return plan.organizationId === orgCopy.id;
                      }));
                      IdentityService.set('currentOrganization', orgCopy);
                      ManagerStore.syncSubscribe();

                      if (userRecordWithSites.userSites && userRecordWithSites.userSites.length) {
                        if ($state.is('no-projects')) {
                          $state.go('dash.map');
                        }
                      }

                      UserStore.setUser(_.assign({}, userRecord, userRecordWithSites));
                      if (UserStore.wasLoggedAtStart()) {
                        MetricsService.track('appReady', 'fullUserObjectReady');
                      }
                    });
                  };

                  $rootScope.$on('AFTER_MAP_LOADED', () => {

                    // the datacapture data and tiles url are ready on the map, the tiles
                    // are are currently downloading
                    const delayFunc = window.requestIdleCallback || $timeout;
                    delayFunc(loadRemainingData);
                  });

                  UserStore.setUser(userRecord);
                  if (UserStore.wasLoggedAtStart()) {
                    MetricsService.track('appReady', 'userObjectReady');
                  }
                  deferred.resolve();
                });
              }
              else {
                $state.go('login', params);
                deferred.reject();
              }
            }
            else {
              $state.go('login', params);
              deferred.reject();
            }
          })

          return deferred.promise;
        }
      ],
      siteParams: [
        '$location', '$state', '$stateParams', 'AnalyticsService', 'authenticate', 'ContourPreviewService', 'SiteStore',
        ($location, $state, $stateParams, AnalyticsService, authenticate, ContourPreviewService, SiteStore) => {
          if ($stateParams.urlUpdate) {
            return;
          }

          // emits tracking event for initial page load of a data capture
          const sendDataCaptureAnalytics = (datacapture) => {
            AnalyticsService.track('[CORE] Data Captures', 'View Data Capture', {
              dataCaptureId: datacapture.id,
              siteId: datacapture.siteId,
              siteName: datacapture.site.name
            });
          };

          if ($stateParams.changeToLocation === document.location.pathname.slice(1) && window.CACHE_siteParamsReturnValue) {
            return window.CACHE_siteParamsReturnValue;
          }

          const siteParamsReturnValue = SiteStore.fetchNewStateData($stateParams)
          .then((currentResources) => {

            if (currentResources && currentResources.hasOwnProperty('site') && currentResources.hasOwnProperty('dataCapture') && currentResources.dataCapture && currentResources.dataCapture.hasOwnProperty('processingJob')) {
              ContourPreviewService.setData({
                site: currentResources.site,
                dataCapture: currentResources.dataCapture,
                uuid: currentResources.dataCapture.processingJob.uuid
              });
            }

            var urlLocation = 'site/';

            urlLocation += currentResources.site.slug;

            if (currentResources.dataCapture) {
              urlLocation += `/dc/${currentResources.dataCapture.id}`;

              if ($location.absUrl().split('?')[1]) {
                urlLocation += `?${$location.absUrl().split('?')[1]}`;
              }

              // if there's no changeToLocation, that means this is an initial app load so we should send the analytics event
              if (!$stateParams.changeToLocation && $stateParams.siteName) {
                sendDataCaptureAnalytics(currentResources.dataCapture);
              }
              else if ($stateParams.siteName) {
                // if there is a changeToLocation, this will fire twice. once for the original page and again for the destination page.
                // we want the analytics to only send once, so filter for the destination routing event to send the analytics.
                const siteNameFromLocation = $stateParams.changeToLocation.split('/')[1];

                if (siteNameFromLocation === $stateParams.siteName) {
                  sendDataCaptureAnalytics(currentResources.dataCapture);
                }
              }
            }

            SiteStore.emit(SiteStore.Events.Viewing);

            $stateParams.changeToLocation = urlLocation;
            // Reset the state params. For some reason they will persist if the
            // same state is called again (angular!)
            $stateParams.siteName = null;
            $stateParams.dataCaptureId = null;
            $stateParams.urlUpdate = null;
          })
          .catch((err) => {
            console.warn(err);
            $state.go('404');
          });

          window.CACHE_siteParamsReturnValue = siteParamsReturnValue;
          return siteParamsReturnValue;
        }
      ]
    },
    views: {
      'map': {
        controller: 'MapController',
        controllerAs: 'vm',
        templateUrl: 'dash/map/map.tpl.html'
      }
    }
  })
  .state('404', {
    controller: 'ErrorPageController',
    controllerAs: 'vm',
    templateUrl: 'dash/error/404.tpl.html',
    url: '/404',
    resolve: {
      authenticate: [
        '$state', '$q', '$timeout', 'AuthService',
        checkAuthentication
      ],
      organization: [
        'sbFlux',
        loadOrganization
      ]
    }
  })
  .state('no-projects', {
    controller: 'ErrorPageController',
    controllerAs: 'vm',
    templateUrl: 'dash/error/noprojects.tpl.html',
    url: '/no-projects',
    resolve: {
      authenticate: [
        '$state', '$q', '$timeout', 'AuthService',
        checkAuthentication
      ],
      organization: [
        'sbFlux',
        loadOrganization
      ]
    }
  });
}

DashRoutes.$inject = ['$urlRouterProvider', '$stateProvider', '$urlMatcherFactoryProvider', '$locationProvider'];

const routing = angular.module('dash.routing', ['app.resources', 'dash.userStore', 'dash.siteStore']);

routing.config(DashRoutes);



// WEBPACK FOOTER //
// ./src/app/dash/routing/index.js